import type { Listing } from '../types';

export type AttributeGroup = {
    title: string;
    items: string[];
};

export type AttributeSection = {
    title: string;
    groups: AttributeGroup[];
};

// --- Slug → label maps per attribute group ---

const EQUIPMENT_LABELS: Record<string, string> = {
    'vyhradene-parkovanie': 'Vyhradené parkovanie',
    'zahrada': 'Záhrada',
    'recepcia': 'Recepcia',
    'zabezpecovacie-zariadenie': 'Zabezpečovacie zariadenie',
    'strazna-sluzba': 'Strážna služba',
    'bazen': 'Bazén',
    'fitnes': 'Fitness',
    'sauna': 'Sauna',
    'bezbarierovy-pristup': 'Bezbariérový prístup',
    'krb': 'Krb',
    'ina-equipment': 'Iné vybavenie',
    'altanok-posedenie': 'Altánok / posedenie',
    'detske-ihrisko': 'Detské ihrisko',
    'kocikaren': 'Kočikáreň',
    'pracovna': 'Pracovňa',
    'uschovna-bicyklov': 'Úschovňa bicyklov',
    'zimna-zahrada': 'Zimná záhrada',
    'vonkajsi-krb': 'Vonkajší krb',
    'sklenik': 'Skleník',
    'zahradny-domcek': 'Záhradný domček',
    'dielna-equipment': 'Dielňa',
    'nabijacia-stanica-elektromobilov': 'Nabíjacia stanica elektromobilov',
    'komunitna-zahrada': 'Komunitná záhrada',
};

const KITCHEN_LABELS: Record<string, string> = {
    'kuchyna': 'Kuchyňa',
    'digestor': 'Digestor',
    'chladnicka': 'Chladnička',
    'kuchynska-linka': 'Kuchynská linka',
    'kuchynsky-kut': 'Kuchynský kút',
    'mikrovlnka': 'Mikrovlnka',
    'mraznicka': 'Mraznička',
    'rura': 'Rúra',
    'sporak': 'Sporák',
    'umyvacka': 'Umývačka',
    'varna-doska': 'Varná doska',
    'rychlovarna-konvica': 'Rýchlovarná konvica',
    'kavovar': 'Kávovar',
};

const BATHROOM_WC_LABELS: Record<string, string> = {
    'bidet': 'Bidet',
    'kupelnovy-nabytok': 'Kúpeľňový nábytok',
    'pisoar': 'Pisoár',
    'pracka': 'Práčka',
    'sprchovy-kut': 'Sprchový kút',
    'susicka': 'Sušička',
    'van': 'Vaňa',
    'van-rohova': 'Rohová vaňa',
    'vylevka': 'Výlevka',
    'wc-oddelene': 'WC oddelené',
    'wc-spolocne-s-kupelnou': 'WC spoločné s kúpeľňou',
};

const SECURITY_LABELS: Record<string, string> = {
    'alarm': 'Alarm',
    'bezpecnostne-dvere': 'Bezpečnostné dvere',
    'elektronicka-brana': 'Elektronická brána',
    'elektronicky-zamkovy-system': 'Elektronický zámkový systém',
    'kamerovy-system': 'Kamerový systém',
    'videovratnik': 'Video vrátnik',
    'strazna-sluzba-security': 'Strážna služba',
};

const SERVICES_LABELS: Record<string, string> = {
    'internet': 'Internet',
    'kabelova-tv': 'Káblová TV',
    'satelit': 'Satelit',
    'klimatizacia': 'Klimatizácia',
    'podlahove-kurenie': 'Podlahové kúrenie',
    'solar': 'Solárne panely',
    'tepelne-cerpadlo': 'Tepelné čerpadlo',
    'centralne-kurenie': 'Centrálne kúrenie',
};

const OBJECT_CHARACTER_LABELS: Record<string, string> = {
    'dobra-lokalita': 'Dobrá lokalita',
    'dobri-susedia': 'Dobrí susedia',
    'luxusny': 'Luxusný',
    'pekny-vyhlad': 'Pekný výhľad',
    'slnecny': 'Slnečný',
    'tiche-prostredie': 'Tiché prostredie',
    'cenovo-dostupne': 'Cenovo dostupné',
};

const ACCESS_ROAD_LABELS: Record<string, string> = {
    'asfaltova': 'Asfaltová',
    'betonova-cesta': 'Betónová cesta',
    'bez-pristupu': 'Bez prístupu',
    'dlazdena': 'Dlážděná',
    'hlinena-nespevnena': 'Hlinená nespevnená',
    'kamenita': 'Kamenitá',
    'neupravena': 'Neupravená',
    'strkova': 'Štrková',
    'travnata': 'Trávnatá',
    'ina-access-road': 'Iná',
};

const TRANSPORT_LABELS: Record<string, string> = {
    'vytah': 'Výťah',
    'vytah-nakladny': 'Nákladný výťah',
    'dialkovy-autobus': 'Diaľkový autobus',
    'dialnica': 'Diaľnica',
    'letisko': 'Letisko',
    'mhd': 'MHD',
    'pristav': 'Prístav',
    'vlak': 'Vlak',
    'cesta': 'Cesta',
    'trolejbus': 'Trolejbus',
    'cyklisticka-infrastruktura': 'Cyklistická infraštruktúra',
};

const SURROUNDINGS_LABELS: Record<string, string> = {
    'jasle': 'Jasle',
    'nemocnica': 'Nemocnica',
    'obchodne-centrum': 'Obchodné centrum',
    'potraviny': 'Potraviny',
    'skola': 'Škola',
    'skolka': 'Škôlka',
    'siet-obchodov': 'Sieť obchodov',
    'detske-ihriska': 'Detské ihriská',
    'sportove-ihriska': 'Športové ihriská',
    'kino': 'Kino',
    'divadlo': 'Divadlo',
    'restauracia': 'Reštaurácia',
    'ina-surroundings': 'Iné',
    'trhovisko': 'Trhovisko',
    'kaviarne': 'Kaviarne',
    'balikomat': 'Balíkomat',
};

const NEARBY_AMENITIES_LABELS: Record<string, string> = {
    ...SURROUNDINGS_LABELS,
};

const WINDOWS_LABELS: Record<string, string> = {
    'eurookna': 'Eurookná',
    'hlinikove-okna': 'Hliníkové',
    'plastove-okna': 'Plastové',
    'drevene-okna': 'Drevené',
    'ine-okna': 'Iné',
    'fiberglassove': 'Fiberglassové',
    'plast-hlinikove': 'Plast-hliníkové',
    'drevo-hlinikove': 'Drevo-hliníkové',
    'ocelove-okna': 'Oceľové',
};

const ROOFING_LABELS: Record<string, string> = {
    'asfaltovy-sindel': 'Asfaltový šindeľ',
    'eternit': 'Eternit',
    'plech-krytina': 'Plech',
    'pozinkovany-plech': 'Pozinkovaný plech',
    'slama': 'Slama',
    'sindel': 'Šindeľ',
    'skridla': 'Škridla',
    'trapezova': 'Trapézová',
    'bitumenova': 'Bitúmenová',
    'asfaltove-pasy': 'Asfaltové pásy',
    'vlaknocementova': 'Vláknocementová',
    'plastova-krytina': 'Plastová krytina',
    'bridlicova': 'Bridlicová',
};

const FLOORS_LABELS: Record<string, string> = {
    'beton-podlaha': 'Betón',
    'dlazba': 'Dlažba',
    'koberec': 'Koberec',
    'korok': 'Korok',
    'parkety': 'Parkety',
    'plavajuce': 'Plávajúce',
    'pvc': 'PVC',
    'dlazkovica': 'Dlažkovica',
    'bambus': 'Bambus',
};

const STAIRS_LABELS: Record<string, string> = {
    'beton-schody': 'Betónové',
    'drevene-schody': 'Drevené',
    'hlinikove-schody': 'Hliníkové',
    'nerezove-schody': 'Nerezové',
    'sklene-schody': 'Sklenené',
    'vyklopne-schody': 'Výklopné',
    'zelezne-schody': 'Železné',
};

const DOORS_LABELS: Record<string, string> = {
    'drevene-dvere': 'Drevené',
    'drevotrieska': 'Drevotrieska',
    'hlinikove-dvere': 'Hliníkové',
    'laminatove-dvere': 'Laminátové',
    'sklene-dvere': 'Sklenené',
};

const WALLS_LABELS: Record<string, string> = {
    'omiety': 'Omietky',
    'tapety': 'Tapety',
    'sadrokarton': 'Sadrokartón',
    'hole-steny': 'Holé steny',
    'stierka': 'Stierka',
};

const GROUP_LABEL_MAP: Record<string, Record<string, string>> = {
    EQUIPMENT: EQUIPMENT_LABELS,
    KITCHEN: KITCHEN_LABELS,
    BATHROOM_WC: BATHROOM_WC_LABELS,
    SECURITY: SECURITY_LABELS,
    SERVICES: SERVICES_LABELS,
    OBJECT_CHARACTER: OBJECT_CHARACTER_LABELS,
    ACCESS_ROAD: ACCESS_ROAD_LABELS,
    TRANSPORT: TRANSPORT_LABELS,
    SURROUNDINGS: SURROUNDINGS_LABELS,
    NEARBY_AMENITIES: NEARBY_AMENITIES_LABELS,
    WINDOWS: WINDOWS_LABELS,
    ROOFING: ROOFING_LABELS,
    FLOORS: FLOORS_LABELS,
    STAIRS: STAIRS_LABELS,
    DOORS: DOORS_LABELS,
    WALLS: WALLS_LABELS,
};

function resolveAttributeLabel(group: string, slug: string): string {
    return GROUP_LABEL_MAP[group]?.[slug] ?? slug;
}

function buildGroup(
    title: string,
    group: string,
    attributesByGroup: Map<string, string[]>,
): AttributeGroup | null {
    const slugs = attributesByGroup.get(group) ?? [];
    if (!slugs.length) return null;
    return {
        title,
        items: slugs.map((slug) => resolveAttributeLabel(group, slug)),
    };
}

// --- Section definitions ---

const SECTIONS_CONFIG: Array<{
    title: string;
    groups: Array<{ title: string; group: string }>;
}> = [
    {
        title: 'Vybavenie a služby',
        groups: [
            { title: 'Vybavenie', group: 'EQUIPMENT' },
            { title: 'Kuchyňa', group: 'KITCHEN' },
            { title: 'Kúpeľňa / WC', group: 'BATHROOM_WC' },
            { title: 'Bezpečnosť', group: 'SECURITY' },
            { title: 'Poskytované služby', group: 'SERVICES' },
        ],
    },
    {
        title: 'Charakteristika a prístup',
        groups: [
            { title: 'Charakteristika objektu', group: 'OBJECT_CHARACTER' },
            { title: 'Prístupová komunikácia', group: 'ACCESS_ROAD' },
            { title: 'Doprava', group: 'TRANSPORT' },
        ],
    },
    {
        title: 'Okolie',
        groups: [
            { title: 'V blízkosti', group: 'SURROUNDINGS' },
            { title: 'Vybavenie v okolí', group: 'NEARBY_AMENITIES' },
        ],
    },
    {
        title: 'Materiály a konštrukcia',
        groups: [
            { title: 'Okná', group: 'WINDOWS' },
            { title: 'Krytina', group: 'ROOFING' },
            { title: 'Podlahy', group: 'FLOORS' },
            { title: 'Schody', group: 'STAIRS' },
            { title: 'Dvere', group: 'DOORS' },
            { title: 'Steny', group: 'WALLS' },
        ],
    },
];

export function getListingAttributeSections(listing: Listing): AttributeSection[] {
    const attributes = listing.detail?.attributes ?? [];
    if (!attributes.length) return [];

    const byGroup = new Map<string, string[]>();
    for (const attr of attributes) {
        const existing = byGroup.get(attr.group) ?? [];
        existing.push(attr.value);
        byGroup.set(attr.group, existing);
    }

    const result: AttributeSection[] = [];

    for (const sectionConfig of SECTIONS_CONFIG) {
        const groups: AttributeGroup[] = [];

        for (const groupConfig of sectionConfig.groups) {
            const group = buildGroup(groupConfig.title, groupConfig.group, byGroup);
            if (group) groups.push(group);
        }

        if (groups.length > 0) {
            result.push({ title: sectionConfig.title, groups });
        }
    }

    return result;
}
